// Freeze the page behind any open popup, app-wide.
//
// Popups here are hand-rolled `position: fixed; inset: 0` overlays in dozens
// of components, and none locked what was behind them, so a wheel over the
// backdrop (or a scroll that ran off the end of the popup) scrolled the page
// underneath. Fixing each popup would never stay fixed, so this one observer
// does it, the same way fitFloating handles menus:
//
//   - an "overlay" is an element with inline `position: fixed` that covers
//     the viewport and has a visible background (a backdrop or a full-screen
//     sheet), or is marked role="dialog"/aria-modal;
//   - while at least one is open, the page root (<html>) and every scrollable
//     ancestor of each overlay get `overflow: hidden`. The ancestors matter: a
//     wheel over a backdrop scrolls the backdrop's nearest scrollable DOM
//     ancestor, which is often an inner panel, not the document;
//   - the overlay itself gets `overscroll-behavior: contain` so scrolling to
//     the end of the popup doesn't chain outward;
//   - when the last overlay closes, exactly the inline values it changed are
//     restored. <body> is left alone: Dashboard and Prospects lock it
//     themselves and restore their own saved value.
//
// Opt out with `data-no-scroll-lock` on the overlay. Installed once from
// index.tsx (installModalScrollLock).

const LOCKED = "data-scroll-locked";
const PREV = "data-scroll-prev";

function isOverlay(el: HTMLElement): boolean {
  if (el.style.position !== "fixed" || el.hasAttribute("data-no-scroll-lock")) return false;
  const r = el.getBoundingClientRect();
  if (r.width < window.innerWidth - 2 || r.height < window.innerHeight - 2) return false;
  if (el.getAttribute("role") === "dialog" || el.getAttribute("aria-modal") === "true") return true;
  const bg = getComputedStyle(el).backgroundColor;
  return !!bg && bg !== "transparent" && !/rgba\([^)]*,\s*0\)$/.test(bg);
}

function lock(el: HTMLElement) {
  if (el.hasAttribute(LOCKED)) return;
  el.setAttribute(LOCKED, "");
  el.setAttribute(PREV, el.style.overflow);
  el.style.overflow = "hidden";
}

function unlockAll() {
  document.querySelectorAll<HTMLElement>(`[${LOCKED}]`).forEach(el => {
    el.style.overflow = el.getAttribute(PREV) || "";
    el.removeAttribute(LOCKED);
    el.removeAttribute(PREV);
  });
}

function scrollableAncestors(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    // One we already locked reads overflow:hidden now; still wanted, or the
    // next pass would release it and re-lock it in a loop.
    if (p.hasAttribute(LOCKED)) { out.push(p); continue; }
    const oy = getComputedStyle(p).overflowY;
    if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) out.push(p);
  }
  return out;
}

function sync() {
  const overlays = Array.from(document.querySelectorAll<HTMLElement>('[style*="fixed"]')).filter(isOverlay);
  if (overlays.length === 0) { unlockAll(); return; }
  const want = new Set<HTMLElement>([document.documentElement]);
  for (const o of overlays) {
    // Only write when it differs: every write is a style mutation, which
    // would re-trigger this observer.
    if (o.style.overscrollBehavior !== "contain") o.style.overscrollBehavior = "contain";
    for (const a of scrollableAncestors(o)) want.add(a);
  }
  // Release anything no longer behind an open overlay, then lock the rest.
  document.querySelectorAll<HTMLElement>(`[${LOCKED}]`).forEach(el => {
    if (!want.has(el)) {
      el.style.overflow = el.getAttribute(PREV) || "";
      el.removeAttribute(LOCKED);
      el.removeAttribute(PREV);
    }
  });
  want.forEach(lock);
}

let installed = false;
export function installModalScrollLock(): void {
  if (installed || typeof window === "undefined" || typeof MutationObserver === "undefined") return;
  installed = true;
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; sync(); });
  };
  // Overlays mount/unmount (childList) or toggle via inline style changes.
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
  window.addEventListener("resize", schedule);
}
